import { Button, Tag } from '@gympass/yoga';
import { Time } from '@gympass/yoga-icons';
import { differenceInCalendarDays } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { ReasonType } from 'services/specialist';

import { TableColumn } from 'components';
import { TabName } from 'components/CPDashboardTab/types';
import { PatientInfo } from 'pages/CareProviderDashboard/types';

import { ConfigParams } from './types';

import * as S from './styles';

const cpDashboardTableConfig = ({
  intl,
  formatToDDMMYYYYString,
  handleRediretToPatientProfile,
}: ConfigParams) => {
  const linkedFilterColumns: TableColumn[] = [
    {
      dataKey: 'fullName',
      key: 'enduser_fullname',
      title: intl.formatMessage({
        id: 'CARE_PROVIDER.DASHBOARD.TABLE.NAME',
      }),
      render: (fullName: string, patient: PatientInfo) => {
        return (
          <S.FullnameText
            data-testid="patientFullName"
            onClick={() => handleRediretToPatientProfile(patient.cognitoId)}
          >
            {fullName}
          </S.FullnameText>
        );
      },
      sortable: true,
      sortTooltip: {
        enable: true,
        ascText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.NAME.SORT_TOOLTIP.ASC',
        }),
        descText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.NAME.SORT_TOOLTIP.DESC',
        }),
      },
    },
    {
      dataKey: 'classificationDate',
      key: 'enduser_classification_date',
      title: intl.formatMessage({
        id: 'CARE_PROVIDER.DASHBOARD.TABLE.CLASSIFICATION_DATE',
      }),
      render: (classificationDate: string) => {
        const data = formatToDDMMYYYYString(classificationDate);
        return data || '-';
      },
      sortable: true,
      defaultSort: 'DESC',
      sortType: 'date',
      sortTooltip: {
        enable: true,
        ascText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.CLASSIFICATION_DATE.SORT_TOOLTIP.ASC',
        }),
        descText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.CLASSIFICATION_DATE.SORT_TOOLTIP.DESC',
        }),
      },
    },
    {
      dataKey: 'nextSession',
      key: 'enduser_next_session',
      title: intl.formatMessage({
        id: 'CARE_PROVIDER.DASHBOARD.TABLE.NEXT_SESSION',
      }),
      render: (nextSession: string) => {
        const data = formatToDDMMYYYYString(nextSession);
        return data || '-';
      },
      sortable: true,
      sortType: 'date',
      sortTooltip: {
        enable: true,
        ascText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.NEXT_SESSION.SORT_TOOLTIP.ASC',
        }),
        descText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.NEXT_SESSION.SORT_TOOLTIP.DESC',
        }),
      },
    },
    {
      dataKey: 'actions',
      title: '',
      key: 'enduser_button_actions',
      render: (_, patient: PatientInfo) => {
        return (
          <Button
            small
            data-testid="rediretToPatientProfile"
            onClick={() => handleRediretToPatientProfile(patient.cognitoId)}
          >
            <FormattedMessage id="CARE_PROVIDER.DASHBOARD.TABLE.SEE_PROFILE" />
          </Button>
        );
      },
    },
  ];

  const unlinkFilterColumns: TableColumn[] = [
    {
      dataKey: 'fullName',
      key: 'enduser_fullname',
      title: intl.formatMessage({
        id: 'CARE_PROVIDER.DASHBOARD.TABLE.NAME',
      }),
      render: (fullName: string, patient: PatientInfo) => {
        return (
          <S.FullnameText
            data-testid="patientFullName"
            onClick={() => handleRediretToPatientProfile(patient.cognitoId)}
          >
            {fullName}
          </S.FullnameText>
        );
      },
      sortable: true,
      sortTooltip: {
        enable: true,
        ascText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.NAME.SORT_TOOLTIP.ASC',
        }),
        descText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.NAME.SORT_TOOLTIP.DESC',
        }),
      },
    },
    {
      dataKey: 'reason',
      key: 'unlink_reason',
      title: intl.formatMessage({
        id: 'CARE_PROVIDER.DASHBOARD.TABLE.REASON',
      }),
      render: (reason: ReasonType) => {
        const types = {
          eligibility_shutdown: (
            <FormattedMessage id="CARE_PROVIDER.DASHBOARD.TABLE.REASON.ELIGIBILITY_SHUTDOWN" />
          ),
          company_shutdown: (
            <FormattedMessage id="CARE_PROVIDER.DASHBOARD.TABLE.REASON.COMPANY_SHUTDOWN" />
          ),
        };

        return types[reason] || '-';
      },
      sortable: true,
      sortTooltip: {
        enable: true,
        ascText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.REASON.SORT_TOOLTIP.ASC',
        }),
        descText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.REASON.SORT_TOOLTIP.DESC',
        }),
      },
    },
    {
      dataKey: 'userExpiredAt',
      key: 'unlink_userExpiredAt',
      title: intl.formatMessage({
        id: 'CARE_PROVIDER.DASHBOARD.TABLE.USER_EXPIRED_AT',
      }),
      render: (expirationDate: string) => {
        const daysUntilUnlink = differenceInCalendarDays(
          new Date(expirationDate),
          new Date(),
        );
        return (
          <Tag.Informative variant="attention" icon={Time}>
            {daysUntilUnlink} dias até o desvínculo
          </Tag.Informative>
        );
      },
      sortable: true,
      defaultSort: 'DESC',
      sortType: 'date',
      sortTooltip: {
        enable: true,
        ascText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.USER_EXPIRED_AT.SORT_TOOLTIP.ASC',
        }),
        descText: intl.formatMessage({
          id: 'CARE_PROVIDER.DASHBOARD.TABLE.USER_EXPIRED_AT.SORT_TOOLTIP.DESC',
        }),
      },
    },
    {
      dataKey: 'actions',
      title: '',
      key: 'unlink_button_actions',
      render: (_, patient: PatientInfo) => {
        return (
          <Button
            small
            data-testid="rediretToPatientProfile"
            onClick={() => handleRediretToPatientProfile(patient.cognitoId)}
          >
            <FormattedMessage id="ELIGIBILITYSHUTDOWNCP.BUTTON.PSYCHOLOGICALRECORD" />
          </Button>
        );
      },
    },
  ];

  const columnsFilterType: Record<TabName, TableColumn[]> = {
    [TabName.CRITICAL]: linkedFilterColumns,
    [TabName.WILL_BE_UNLINKED]: unlinkFilterColumns,
  };

  return {
    columnsFilterType,
  };
};

export default cpDashboardTableConfig;
